import "@assets/css/bootstrap-grid.css";
import { zodResolver } from "@hookform/resolvers/zod";
import { endpoint } from "@services/api.services";
import { METHODS } from "@services/interface.service";
import { useQueryClient } from "@tanstack/react-query";
import { FormSkeleton } from "@ui/external/components/skeleton";
import Tooltip from "@ui/external/components/tooltip";
import { getInputField } from "@utils/function/index.function";
import {
  changeSymbolstoDash,
  makeSlug,
  removeHttpPrefix,
  showToast,
  throwErrorAlert,
} from "@utils/helper/index.helper";
import { useHandleAction, usePathname } from "@utils/hook/index.hook";
import { FormField } from "@utils/interface/form.interface";
import {
  TestimonialVideoFormTypes,
  testimonialVideoSchema,
} from "@utils/interface/form.schema";
import { IndividualQuery } from "@utils/interface/index.interface";
import { TestimonialData } from "@utils/interface/journey.interface";
import { useListData, usePostData } from "@utils/query/index.query";
import { usePending, useSelectionStore } from "@utils/states/index.state";
import { InfoIcon } from "lucide-react";
import React, { useEffect, useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

const VideoTestimonialForm: React.FC = () => {
  const pathname = usePathname();
  const { searchParams, handleAction } = useHandleAction({});
  const queryClient = useQueryClient();
  const { setIsFormPending } = useSelectionStore();

  /* avoid repetation */
  const pageItem = searchParams.get("page-item");
  const pageMode = searchParams.get("page");

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<TestimonialVideoFormTypes>({
    mode: "onChange",
    resolver: zodResolver(testimonialVideoSchema),
  });
  useEffect(() => {
    if (pageMode === "add") {
      setValue("slug", changeSymbolstoDash(makeSlug(watch("name"))));
    }
  }, [watch("name")]);
  const { data: fetchData, isPending: fetchPending } = useListData<
    IndividualQuery<TestimonialData>
  >({
    endpoint: endpoint["testimonial"],
    id: pageItem || "",
    key: "testimonial",
    params: {
      id: pageItem || "",
    },
    enabled: !!pageItem, //fetch if exist
  });

  const handleMutate = (
    data: TestimonialVideoFormTypes & {
      id: string | number;
      image?: any;
      video?: any;
      testimonial_type: string;
    }
  ) => {
    if (pageItem) {
      data = {
        ...data,
        id: pageItem || "",
        testimonial_type: "video",
      };
    }
    // if (!(data.image instanceof File || data.image instanceof Blob))
    //   delete data.image;
    // if (!(data.video instanceof File || data.video instanceof Blob))
    //   delete data.video;
    data = {
      ...data,
      testimonial_type: "video",
    };
    // if (data.video_url) {
    //   data.video_url = "https://" + removeHttpPrefix(data.video_url as string);
    // }

    return {
      data,
      endpoint: endpoint["testimonial"],
      method: pageItem ? METHODS.PUT : METHODS.POST,
    };
  };
  console.log("Error", errors);
  const handleSuccess = async ({ status }: { data: any; status: number }) => {
    setIsFormPending(false);
    if ([200, 201].includes(status)) {
      queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
      queryClient.invalidateQueries({
        queryKey: [`${pathname}-listing-scroll`],
      });

      handleAction({ type: "page-close" });
      showToast({
        description: `${pathname} ${status === 200 ? "Updated" : "Created"}`,
        type: "success",
        title: "Success",
      });
    } else {
      return new Error("Unknown API Error");
    }
  };
  const handleError = async (error: any) => {
    setIsFormPending(false);
    console.log(error, "error00");
    if (error?.data?.status == 413) {
      showToast({
        description: "File Size Too Large",
        type: "error",
        title: "Error",
      });
    }
    const errorMessage = error.data.response.data;
    throwErrorAlert(errorMessage);
  };
  /* set default value */
  const { setIsRetrive }: any = usePending();

  useEffect(() => {
    setIsRetrive(pageItem ? fetchPending : false);
    if (fetchData?.status === 200 && !fetchPending) {
      const firstResult = fetchData?.data;
      if (pageMode === "add") {
        return reset({});
      } else if (firstResult) {
        reset(() => ({
          description: firstResult?.description || "",
          image: firstResult?.image || "",
          name: firstResult?.name || "",
          slug: firstResult?.slug || "",
          role: firstResult?.role || "",
          video: firstResult?.video || "",
          video_url: removeHttpPrefix(firstResult?.video_url) || "",
          header_code: firstResult?.header_code || "",
          footer_code: firstResult?.footer_code || "",
        }));
      }
    }
  }, [fetchData?.data, fetchPending, pageItem]);

  const { mutate } = usePostData();

  const onSubmit: SubmitHandler<TestimonialVideoFormTypes> = (data: any) => {
    setIsFormPending(true);
    mutate(handleMutate(data), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
  };

  const videoFormSchema: FormField[] = useMemo(
    () => [
      {
        name: "name",
        label: "Name",
        colSpan: 6,
        type: "text",
        placeholder: "Enter Name",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "slug",
        label: "Slug",
        colSpan: 6,
        type: "text",
        placeholder: "Enter Slug",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "role",
        label: "Role",
        colSpan: 6,
        type: "text",
        placeholder: "Enter Role",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "video",
        label: "Upload Video",
        upload_type: "video",
        colSpan: 7,
        type: "cropper",
        control: control,
        setValue: setValue,
        isMulti: false,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "image",
        label: "Upload Image",
        colSpan: 5,
        type: "cropper",
        control: control,
        setValue: setValue,
        errors,
        isMulti: false,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
      name: "header_code",
      label: "Header Code ",
      colSpan: 12,
      type: "textarea",
      placeholder: "Description Here",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full  w-full`,
      visable: true,
    },
    {
      name: "footer_code",
      label: "Footer Code ",
      colSpan: 12,
      type: "textarea",
      placeholder: "Description Here",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full  w-full`,
      visable: true,
    },

      // {
      //   name: "video_url",
      //   label: (
      //     <div className="flex gap-2 items-center">
      //       Video Link{" "}
      //       <Tooltip description="Optional Video Link">
      //         <InfoIcon size={12} />
      //       </Tooltip>
      //     </div>
      //   ),
      //   colSpan: 6,
      //   type: "prefixinput",
      //   placeholder: "Enter Video Link",
      //   control: control,
      //   setValue: setValue,
      //   errors,
      //   onChange: () => {
      //     clearErrors("video");
      //   },
      //   className: `mb-[20px] min-w-full w-full`,
      //   visable: false,
      // },
    ],
    [control, errors, setValue]
  );

  if (fetchPending && pageItem)
    return (
      <>
        <FormSkeleton />
      </>
    );

  return (
    <>
      <form
        id={pathname + "video"}
        onSubmit={handleSubmit(onSubmit)}
        className="row w-full"
      >
        {videoFormSchema.map(
          (field, index) =>
            (field.visable || field.visable == undefined) && (
              <div
                key={index}
                className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
              >
                {getInputField({ ...field })}
              </div>
            )
        )}
      </form>
    </>
  );
};

export default VideoTestimonialForm;
